import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OtpAuthComponent } from '../otp-auth/otp-auth.component';
import { AppStateService } from '../app-state.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { HeaderComponent } from "../header/header.component";
import { environment } from '../../environments/environment';
import { AppointmentService } from '../registration/appointment.service';
import { FormConfigService } from '../registration/forms-config.service';

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OtpAuthComponent,
    HeaderComponent
],
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss'
})
export class AuthComponent  {
  phoneNumber: string = '';
  patientId: string = '';
  otpVerifyInProgress = false;

  appStateService = inject(AppStateService);
  snackBar = inject(MatSnackBar);
  router = inject(Router);
  authService = inject(AuthService);
  appointmentService = inject(AppointmentService);
  formService = inject(FormConfigService);

  constructor() {
    this.phoneNumber = this.appStateService.getState().phoneNumber || '';
    this.patientId = this.appStateService.getState().patientId || '';

  }

  ngOnInit(): void {
     // get authentication token if available from local storage
     let idToken = sessionStorage.getItem('idToken');
     if (environment.debug) {
        // idToken = 'eyJhbGciOiJSUzI1NiIsImtpZCI6Ijc5M2Y3N2Q0N2ViOTBiZjRiYTA5YjBiNWFkYzk2ODRlZTg1NzJlZTYiLCJ0eXAiOiJKV1QifQ.eyJpc3MiOiJodHRwczovL3NlY3VyZXRva2VuLmdvb2dsZS5jb20vcHJvamVjdC0wMDEtMjgzNTIwIiwiYXVkIjoicHJvamVjdC0wMDEtMjgzNTIwIiwiYXV0aF90aW1lIjoxNzIwMTk0OTkxLCJ1c2VyX2lkIjoiNjBSYnA2MkRjWE1ZQzNXQTZQSUdObGlYRVRvMSIsInN1YiI6IjYwUmJwNjJEY1hNWUMzV0E2UElHTmxpWEVUbzEiLCJpYXQiOjE3MjAxOTQ5OTEsImV4cCI6MTcyMDE5ODU5MSwicGhvbmVfbnVtYmVyIjoiKzE0MDgzMjk4MjI1IiwiZmlyZWJhc2UiOnsiaWRlbnRpdGllcyI6eyJwaG9uZSI6WyIrMTQwODMyOTgyMjUiXX0sInNpZ25faW5fcHJvdmlkZXIiOiJwaG9uZSJ9fQ.Tb-HXKUwSsVllzB5cai24TiKo0kmSXwkdaz68tQ7pngCWVFPkOmbroKF0XgFOzGhrpaVtJmKRp5d1OeIVWr8i7KJx4VBfNiscpgQVC__-4hiiuNTMJAJnvnPGKxwvVLW9Y4NQ1hFb4kyGBSKbmS3BIQfdUhAxmauquyD0A9i60yhidrbVb2YwCjTD4yIa3wsjQ-XsP__S67mVhaWGg-bdW8Tb01Y548ZqrACVXugFpKyyXP73oCLtU29vWOgxOhmmMOryx3zJ_ZvZXWHg9Tm_xtrb0Hy86htNV7zPb5q_W08R4fX050_bJQ5VMJoHzrQUyzFWhR471nBKsqKZOy8ng'
      }
     if (idToken && idToken !== '') {
       this.onOtpVerified(idToken);
       return;
     }
     if (!this.patientId || this.patientId === '') {
       this.authService.logout();
       return
     }

     if (!this.phoneNumber || this.phoneNumber === '') {
       this.authService.logout();
       return;
     }
     this.otpVerifyInProgress = true;
  }

  onOtpVerified(event: string): void {

    if (this.otpVerifyInProgress) {
      this.otpVerifyInProgress = false;
    }
    if (!event || event==='') {
      return;
    }
    this.patientId = sessionStorage.getItem('patientId') || '';
    if (!this.patientId || this.patientId === '') {
      this.authService.logout();
      return;
    }
    const idTokenStr = event;
    sessionStorage.setItem('idToken', idTokenStr); // Store the ID token
    this.authService.login(this.patientId, idTokenStr).subscribe({
      next: (response: any) => {
        this.authService.setToken(response.access_token);
        this.authService.setRefreshToken(response.refresh_token);
        this.appStateService.setState({
          patientId: this.patientId,
        });

        // Check if there's a pending appointment to book
        // Only process appointment data if it's from the current session (has required fields)
        const appointmentData = this.appStateService.getState().appointmentData;
        if (appointmentData && appointmentData.appointmentDateTime && appointmentData.operatoryNumber) {
          // Check if this is a new patient - they need to complete registration first
          if (appointmentData.patientType === 'new-emergency') {
            this.checkRegistrationStatus();
          } else {
            // Existing patient - auto-book the appointment
            this.bookPendingAppointment(appointmentData);
          }
        } else {
          // No appointment data or incomplete data - clear any stale data and navigate to dashboard
          if (appointmentData) {
            this.appStateService.setState({ appointmentData: null });
          }
          this.router.navigate(['/dashboard']);
        }
      },
      error: (error: any) => {
        this.snackBar.open('Login failed', 'Close', { duration: 3000 });
        this.authService.logout();
      }
    });
  }

  private bookPendingAppointment(appointmentData: any): void {
    const clientId = this.appStateService.getState().clientId;
    const orgId = this.appStateService.getState().orgId;

    const bookingPayload = {
      clientId: clientId,
      orgId: orgId,
      patientId: this.patientId,
      appointmentDateTime: appointmentData.appointmentDateTime,
      appointmentTypeNumber: appointmentData.appointmentTypeNumber,
      operatoryNumber: appointmentData.operatoryNumber,
      lengthInMinutes: appointmentData.lengthInMinutes,
      summary: appointmentData.summary || '',
      isNewPatient: appointmentData.isNewPatient,
      patientType: appointmentData.patientType
    };

    this.snackBar.open('Booking your appointment...', '', { duration: 2000 });

    this.appointmentService.createAppointment(bookingPayload).subscribe({
      next: (response: any) => {

        this.snackBar.open('Appointment booked successfully!', 'Close', { duration: 5000 });

        // Keep the appointment data for displaying on the confirmation page
        // Set appointmentBooked flag to show confirmation instead of booking form
        // Don't clear appointmentData - we need it to display the confirmation
        
        // Update the appointment data to include the appointment ID from response
        const updatedAppointmentData = {
          ...appointmentData,
          appointmentId: response.appointmentId || response.id
        };
        
        this.appStateService.setState({
          redirectUrl: '',  // Clear redirect URL to prevent auto-navigation to scheduler
          appointmentBooked: true,  // Flag indicating appointment is already booked
          appointmentData: updatedAppointmentData  // Keep appointment data with ID
        });

        // Navigate to the appointment confirmation page
        // It will display the booked appointment details
        this.router.navigate(['/dashboard/appointment', 'new']);
      },
      error: (error: any) => {
        this.snackBar.open('Failed to book appointment. Please try again from the dashboard.', 'Close', { duration: 5000 });

        // Still navigate to dashboard so user isn't stuck
        this.router.navigate(['/dashboard']);
      }
    });
  }

  private checkRegistrationStatus(): void {
    const clientId = this.appStateService.getState().clientId || '';
    const appointmentData = this.appStateService.getState().appointmentData;
    
    this.formService.fetchPatient(clientId, this.patientId).subscribe({
      next: (patientData) => {
        
        // For new emergency patients with appointment data, let them book appointment first
        if (appointmentData && appointmentData.patientType === 'new-emergency') {
          this.bookPendingAppointment(appointmentData);
        } else {
          // For other cases, navigate to dashboard
          this.router.navigate(['/dashboard']);
        }
      },
      error: (error) => {
        // On error, default to dashboard
        this.router.navigate(['/dashboard']);
      }
    });
  }

  ngOnDestroy(): void {
  }

}
